import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Select, DatePicker, message, Card, Typography } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../api/api';
import { fetchLanguages } from '../../features/Languages/languageSlice';
import { fetchServices } from '../../features/Services/serviceSlice';
import { SmileOutlined } from '@ant-design/icons';
import './ProjectRequestPage.css'; // Import custom CSS for animations
import logo from '../../assets/WhatsApp_Image_2025-01-20_at_9.35.44_pm-4-removebg-preview.png';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const ProjectRequestPage = () => {
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false); // Track submission state
  const [errors, setErrors] = useState({}); // Store validation errors
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  // Select languages and services from the Redux store
  const languages = useSelector((state) => state.languages.languages);
  const services = useSelector((state) => state.services.services);

  // Fetch languages and services on mount
  useEffect(() => {
    if (!languages || languages.length === 0) {
      dispatch(fetchLanguages());
    }
    if (!services || services.length === 0) {
      dispatch(fetchServices());
    }
  }, [dispatch, languages, services]);

  const onFinish = async (values) => {
    const formData = new FormData();
    formData.append('project_name', values.project_name);
    formData.append('description', values.description);
    formData.append('expected_date', values.expected_date.format('YYYY-MM-DD'));
    formData.append('note', values.note);
    formData.append('source_language_id', values.source_language_id);
    formData.append('client_name', values.client_name);
    formData.append('business_name', values.business_name);
    formData.append('email', values.email);
    formData.append('phone', values.phone);

    // Append each selected target language and service
    values.target_languages.forEach((lang) => formData.append('target_languages[]', lang));
    values.services.forEach((service) => formData.append('services[]', service));

    // Append the file if it exists
    if (file) {
      formData.append('file', file);
    }

    setLoading(true);
    try {
      const response = await api.post('/project-request', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success('Project request submitted successfully!');
      setSubmitted(true); // Set submission state to true
      form.resetFields();
      setFile(null);
      setErrors({}); // Clear any previous errors
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Clear the file input
      }
    } catch (error) {
      if (error.response && error.response.data.errors) {
        setErrors(error.response.data.errors); // Set validation errors
      } else {
        message.error('Failed to submit project request. Please try again.');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    } else {
      setFile(null);
    }
  };

  return (
    <div className="project-request-container">
      {/* Header with Logo */}
      <div className="header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={logo} alt="Company Logo" className="logo" />
      </div>

      {submitted ? (
        <Card className="success-card">
          <SmileOutlined className="success-icon" />
          <Title level={3}>Thank You!</Title>
          <p>Your project request has been submitted successfully. We will contact you via email within 24 hours.</p>
        </Card>
      ) : (
        <Card className="form-card">
          <Title level={1}>Project Request Form</Title>
          <Title level={4}> fill the form below to submit a project request and you
             will recieve email
            for a quotation within 24 hours.  
          </Title>
          <br></br>
          
          <Form form={form} onFinish={onFinish} layout="vertical">
          <Title level={4}>
            Client Data  
          </Title>
          <Form.Item
              name="client_name"
              label="Client Name"
              validateStatus={errors.client_name ? 'error' : ''}
              help={errors.client_name ? errors.client_name[0] : ''}
              rules={[{ required: true, message: 'Please enter the Client name' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="email"
              label="Client Email"
              validateStatus={errors.email ? 'error' : ''}
              help={errors.email ? errors.email[0] : ''}
              rules={[{ required: true, message: 'Please enter the Client Email' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="business_name"
              label="Business Name"
              validateStatus={errors.business_name ? 'error' : ''}
              help={errors.business_name ? errors.business_name[0] : ''}
              rules={[{ required: true, message: 'Please enter the Business Name' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="phone"
              label="Client phone"
              validateStatus={errors.phone ? 'error' : ''}
              help={errors.phone ? errors.phone[0] : ''}
              rules={[{ required: true, message: 'Please enter the Client phone' }]}
            >
              <Input type="number" />
            </Form.Item>
            <Form.Item
              name="project_name"
              label="Project Name"
              validateStatus={errors.project_name ? 'error' : ''}
              help={errors.project_name ? errors.project_name[0] : ''}
              rules={[{ required: true, message: 'Please enter the project name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              validateStatus={errors.description ? 'error' : ''}
              help={errors.description ? errors.description[0] : ''}
              rules={[{ required: true, message: 'Please enter a description' }]}
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              name="expected_date"
              label="Expected Date"
              validateStatus={errors.expected_date ? 'error' : ''}
              help={errors.expected_date ? errors.expected_date[0] : ''}
              rules={[{ required: true, message: 'Please select the expected date' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="note"
              label="Note"
              validateStatus={errors.note ? 'error' : ''}
              help={errors.note ? errors.note[0] : ''}
            >
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item
              name="source_language_id"
              label="Source Language"
              validateStatus={errors.source_language_id ? 'error' : ''}
              help={errors.source_language_id ? errors.source_language_id[0] : ''}
              rules={[{ required: true, message: 'Please select the source language' }]}
            >
              <Select placeholder="Select source language">
                {languages && languages.length > 0 ? (
                  languages.map((lang) => (
                    <Option key={lang.id} value={lang.id}>
                      {lang.name}
                    </Option>
                  ))
                ) : (
                  <Option disabled>Loading languages...</Option>
                )}
              </Select>
            </Form.Item>

            <Form.Item
              name="target_languages"
              label="Target Languages"
              validateStatus={errors.target_languages ? 'error' : ''}
              help={errors.target_languages ? errors.target_languages[0] : ''}
              rules={[{ required: true, message: 'Please select target languages' }]}
            >
              <Select mode="multiple" placeholder="Select target languages">
                {languages && languages.length > 0 ? (
                  languages.map((lang) => (
                    <Option key={lang.id} value={lang.id}>
                      {lang.name}
                    </Option>
                  ))
                ) : (
                  <Option disabled>Loading languages...</Option>
                )}
              </Select>
            </Form.Item>

            <Form.Item
              name="services"
              label="Services"
              validateStatus={errors.services ? 'error' : ''}
              help={errors.services ? errors.services[0] : ''}
              rules={[{ required: true, message: 'Please select services' }]}
            >
              <Select mode="multiple" placeholder="Select services">
                {services && services.length > 0 ? (
                  services.map((service) => (
                    <Option key={service.id} value={service.id}>
                      {service.name}
                    </Option>
                  ))
                ) : (
                  <Option disabled>Loading services...</Option>
                )}
              </Select>
            </Form.Item>

            {/* <Form.Item
              name="client_email"
              label="Client Email"
              validateStatus={errors.client_email ? 'error' : ''}
              help={errors.client_email ? errors.client_email[0] : ''}
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input />
            </Form.Item> */}

            <Form.Item
              name="file"
              label="Upload File"
              validateStatus={errors.file ? 'error' : ''}
              help={errors.file ? errors.file[0] : ''}
            >
              <input type="file" ref={fileInputRef} onChange={handleFileChange} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Submit
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}
    </div>
  );
};

export default ProjectRequestPage;